import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom"
import { getUserProfile } from "../api/apiService";
import { 
  Close,
  ExitToApp,
  MenuOpen,
  MenuTwoTone,
  Notifications,
  Person,
  WorkspacePremiumRounded,
  Fullscreen,
  FullscreenExit,
  } from '@mui/icons-material';
import {
  AppBar,
  Box, 
  IconButton, 
  Toolbar, 
  Typography,
  Button,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Badge,
  Grid,
  Tooltip,
  //useTheme,
  } from '@mui/material';
import UsageProgress from "../utils/UsageProgress";

export default function MainHeader({ collapsed, setCollapsed, toggled, setToggled, isAdmin: isAdminProp, subscriptionInfo }) {
  //const theme = useTheme();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  const [userData, setUserData] = useState();

  const [confirmOpen, setConfirmOpen] = useState(false);
  
  //for profile button click
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  //for notification dailog
   const [openDailog, setOpenDailog] = useState(false);

   const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);

  // Fullscreen logic
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // detect admin role
  const computedIsAdmin = (() => {
    const role = userData?.role || userData?.userRole || userData?.type;
    const roles = userData?.roles;
    if (typeof role === 'string' && role.toLowerCase() === 'admin') return true;
    if (Array.isArray(roles) && roles.map(r => String(r).toLowerCase()).includes('admin')) return true;
    if (userData?.isAdmin === true) return true;
    return false;
  })();

  const isAdmin = isAdminProp ?? computedIsAdmin;


  //On mount / page load
  useEffect(()=>{
    /*For fetching user profile details */
    const fetchProfile = () => {
        getUserProfile()
        .then((res) => {
            setUserData(res.data.data);
        })
        .catch((err) => {
            // You are not logged In
            if (process.env.NODE_ENV !== 'production') {
              console.error("Error fetching user profile", err);
            }
            // Session expired
            setSnackbar({ open: true, message: 'Session expired. Please log in again.', severity: 'warning' });

            //handling Auth token expiration
            localStorage.removeItem("token");
            localStorage.removeItem("clientId");

            navigate("/");
        })
    };

    fetchProfile(); 
  },[navigate]);

  /*Logout button click */
  const handleLogout = () =>{
    //if (!window.confirm('Are you sure you want to Logout?')) return;
      localStorage.removeItem('token');
      localStorage.removeItem('clientId');
      navigate('/');
      if (process.env.NODE_ENV !== 'production') {
        console.log("Logout Successful");
      }
  }

  /**Side bar toggle */
  const handleToggleClick = () => {
    //Just toggle both for simplicity
    setCollapsed(!collapsed);
    setToggled(!toggled);
    handleClose();
  }

  /* On profile name/icon click */
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClickOpen = () => {
    setOpenDailog(true);
  };
  const handleNotificationClose = () => {
    setOpenDailog(false);
  };



  return(
    <AppBar position="sticky" sx={(theme) => ({
      backgroundImage: theme.palette.gradient.primary,
      color: theme.palette.common.white,
      boxShadow: '0px 8px 24px rgba(2,6,23,0.10)'
    })}>
      {/* Error display via Alert and snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Toolbar>
        <IconButton onClick={handleToggleClick} color='inherit'>
          {collapsed?<MenuTwoTone fontSize="large"/>:<MenuOpen fontSize="large"/>}
        </IconButton>

        {/* <SmartToyOutlined fontSize="medium" sx={{ml:2}}/> */}
        <Typography  variant="h5" sx={{px:1, fontWeight: 600, ml:2}} >
          ThomoBot
        </Typography>

        {isAdmin && (
          <Typography variant="button" color="primary" sx={{px:1, fontWeight: 700, border: "white", borderRadius:2, bgcolor:"white"}}>
            ADMIN
          </Typography>
        )}

        <Box sx={{flexGrow: 1}}/>

        <Tooltip title={isFullscreen? "Back to normal size" : "Full screen"}>
        <IconButton onClick={toggleFullscreen} color="inherit" sx={{ mr: 1 }}>
             {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
        </IconButton>
        </Tooltip>
        {/* Subscription Status Button */}
        {!isAdmin && subscriptionInfo && (
          <>
          {/* Full Button for >= sm screens */}
          <Button
            variant="outlined"
            color= "primary"
            startIcon = {<WorkspacePremiumRounded />}
            sx={{
              display: { xs: 'none', sm: 'flex' },
              textTransform: 'none',
              borderRadius: 3,
              mr: 2,
              bgcolor: "white",
              borderColor: "white",
              "&:hover": { borderColor: "white" }
            }}
            onClick={() => setSubscriptionModalOpen(true)}
          >
            {subscriptionInfo.isActive
              ? `${subscriptionInfo.plan.toUpperCase()} Plan`
              : "Subscription Expired"}
          </Button>

          {/* Icon Only for XS screens */}
          <IconButton
            onClick={() => setSubscriptionModalOpen(true)}
            sx={{
              display: { xs: 'flex', sm: 'none' }
            }}
            color="inherit"
          >
            <WorkspacePremiumRounded />
          </IconButton>
          </>
        )}

        {!isAdmin && (
          <IconButton title='Notification' color='inherit' onClick={handleNotificationClickOpen}>
            <Badge badgeContent={3} color="primary">
              <Notifications/>
            </Badge>
          </IconButton>
        )}
        {/* <IconButton title='Settings' color='secondary'>
          <Settings/>
        </IconButton> */}

        {/* Profile view with logout Button */}
          <Button
            id="basic-button"
            aria-controls={open ? 'basic-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            onClick={handleClick}
            color="inherit"
            //loading={loading}
            //loadingIndicator={<Typography sx={{color:'inherit'}} >Loading...</Typography>}
          >
            <Person/>
            {/* <Typography sx={{px:1}}>{  userData?.given_name || 'N/A'}</Typography> */}
             &#9660;
          </Button>
          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            slotProps={{
              list: {
                'aria-labelledby': 'basic-button',
              },
            }}
          >
            <MenuItem > <Typography>{ userData?.firstName || 'N/A'} {userData?.lastName || 'N/A'}</Typography> </MenuItem>
            <MenuItem ><Typography>{  userData?.email || 'N/A'}</Typography></MenuItem>
            <MenuItem 
            onClick={() => {
              setConfirmOpen(true);
            }}
            >
              <Typography color="error" sx={{display:'flex'}}><ExitToApp/>Logout</Typography>
            </MenuItem>
          </Menu>
      </Toolbar>

      {/* Logout confirmation dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
      >
        <DialogTitle>Confirm Logout</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to Logout?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={async () => {
              await handleLogout();
              setConfirmOpen(false);
            }}
            color="error"
            variant="contained"
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification icon click */}
      {!isAdmin && (
        <Dialog
          onClose={handleClose}
          aria-labelledby="customized-dialog-title"
          open={openDailog}
          fullWidth
          maxWidth="sm"
          slotProps={{ paper :{
            sx: {
              borderRadius: 2,  
            },
          } 
        }}
        >
          <DialogTitle sx={{ mr: 2, pr: 5 }} id="customized-dialog-title">
            Notifications
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleNotificationClose}
            sx={(theme) => ({
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            })}
          >
            <Close />
          </IconButton>
          <DialogContent dividers>
            <Typography gutterBottom>
              Here comes notification 1
            </Typography>
            <Typography gutterBottom>
              Here comes notification 2
            </Typography>
            <Typography gutterBottom>
              Here comes notification 3
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button autoFocus sx={{textTransform: 'none'}}>
              Clear Notifications
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Subscription Details Modal */}
      {subscriptionInfo && (
      <Dialog
        open={subscriptionModalOpen}
        onClose={() => setSubscriptionModalOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Subscription Details
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            background: "#f9fafb",
            p: 3
          }}
        >
          {/* Header Badge */}
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Box
              sx={{
                display: "inline-block",
                px: 2,
                py: 0.5,
                borderRadius: 3,
                bgcolor:
                  subscriptionInfo?.isActive
                    ? "success.light"
                    : "error.light",
                color:
                  subscriptionInfo?.isActive
                    ? "success.dark"
                    : "error.dark",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 0.5
              }}
            >
              {subscriptionInfo?.status || "Unknown Status"}
            </Box>

            <Typography variant="h5" fontWeight={700} mt={1}>
              {subscriptionInfo?.plan?.toUpperCase() || "PLAN"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Subscription Overview
            </Typography>
          </Box>

          {/* Info Card */}
          <Box
            sx={{
              borderRadius: 3,
              p: 3,
              bgcolor: "white",
              boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
              display: "flex",
              flexDirection: "column",
              gap: 2
            }}
          >
            {/* Status Row */}
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography color="text.secondary">Subscription Status</Typography>
              <Typography fontWeight={600} color={subscriptionInfo?.isActive ? "success.main" : "error.main"}>
                {subscriptionInfo?.isActive ? "Active" : "Inactive"}
              </Typography>
            </Box>

            {/* Plan Row */}
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography color="text.secondary">Current Plan</Typography>
              <Typography fontWeight={600}>
                {subscriptionInfo?.plan?.toUpperCase() || "-"}
              </Typography>
            </Box>

            {/* Trial Days */}
            {subscriptionInfo?.plan === "trial" && (
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography color="text.secondary">Trial Days Left</Typography>
                <Typography fontWeight={600} color="warning.main">
                  {subscriptionInfo?.remainingTrialDays} days
                </Typography>
              </Box>
            )}

            {/* Expiry */}
            {subscriptionInfo?.expiryDate && (
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography color="text.secondary">Expiry Date</Typography>
                <Typography fontWeight={600}>
                  {new Date(subscriptionInfo.expiryDate).toLocaleDateString()}
                </Typography>
              </Box>
            )}

            {/* Plan Description */}
            {/* <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" fontWeight={600} mb={0.5}>
                Plan Description
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {subscriptionInfo?.description ||
                  "This plan gives you access to messaging automation, campaign tools, analytics and more."}
              </Typography>
            </Box> */}

            {/* Usage Section */}
            <Box
              sx={{
                mt: 3,
                p: 3,
                borderRadius: 3,
                bgcolor: "white",
                boxShadow: "0 4px 12px rgba(0,0,0,0.06)"
              }}
            >
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                Usage Overview
              </Typography>

              <Grid container spacing={2} sx={{ mt: 2 }}>
                {/* Row 1 */}
                <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                  <UsageProgress
                    label="Leads"
                    used={subscriptionInfo.subscription.currentUsage.leads}
                    limit={subscriptionInfo.subscription.usageLimits.leads}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                  <UsageProgress
                    label="Campaigns"
                    used={subscriptionInfo.subscription.currentUsage.campaigns}
                    limit={subscriptionInfo.subscription.usageLimits.campaigns}
                  />
                </Grid>

                {/* Row 2 */}
                <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                  <UsageProgress
                    label="Emails"
                    used={subscriptionInfo.subscription.currentUsage.emails}
                    limit={subscriptionInfo.subscription.usageLimits.emails}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                  <UsageProgress
                    label="API Calls"
                    used={subscriptionInfo.subscription.currentUsage.apiCalls}
                    limit={subscriptionInfo.subscription.usageLimits.apiCalls}
                  />
                </Grid>
              </Grid>
              {/* <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Team Members Allowed:
                </Typography>
                <Typography fontWeight={600}>
                  {subscriptionInfo.subscription.usageLimits.teamMembers}
                </Typography>
              </Box> */}
            </Box>
          </Box>

          {/* CTA */}
          <Box sx={{ textAlign: "center", mt: 3 }}>
            <Button
              variant="contained"
              size="large"
              sx={{
                textTransform: "none",
                borderRadius: 2,
                px: 4
              }}
              onClick={() => {
                setSubscriptionModalOpen(false);
                navigate("/home/subscription");
              }}
            >
              Manage Subscription
            </Button>
          </Box>
        </DialogContent>

        <DialogActions>
          {/* Navigate user to Subscription Page */}
          <Button onClick={() => setSubscriptionModalOpen(false)}>
            Close
          </Button>
        </DialogActions>

      </Dialog>
      )}
    </AppBar>

    

  );
}
